import { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, openPdf } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

export default function Sales() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", account_id: "", date: todayISO() });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const delSale = async (s) => {
    if (!window.confirm(`Supprimer la vente ${s.sale_number} ? Le véhicule repassera « Disponible », le crédit associé sera supprimé et les encaissements reversés des comptes.`)) return;
    try {
      await api.delete(`/sales/${s.id}`);
      toast.success("Vente supprimée — véhicule remis en stock");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const [newCustomer, setNewCustomer] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", new_customer: { name: "", phone: "", address: "" }, vehicle_id: "",
    sale_price: "", discount: "0", paid_amount: "", payment_method: "Espèces", account_id: "",
    sale_date: todayISO(), installments_count: "1", frequency: "mensuelle", due_date: "", salesperson: "", notes: "",
  });

  const load = () => api.get("/sales").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const finalPrice = useMemo(() => (Number(form.sale_price) || 0) - (Number(form.discount) || 0), [form.sale_price, form.discount]);
  const remaining = useMemo(() => Math.max(finalPrice - (Number(form.paid_amount) || 0), 0), [finalPrice, form.paid_amount]);
  const sellable = vehicles.filter((v) => !["Vendu", "En location", "Réservé"].includes(v.status));

  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Véhicule requis");
    if (!newCustomer && !form.customer_id) return toast.error("Client requis");
    if (newCustomer && !form.new_customer.name) return toast.error("Nom du client requis");
    try {
      await api.post("/sales", {
        ...form,
        sale_price: Number(form.sale_price) || 0,
        discount: Number(form.discount) || 0,
        paid_amount: Number(form.paid_amount) || 0,
        installments_count: Number(form.installments_count) || 1,
        account_id: form.account_id || null,
        due_date: form.due_date || null,
        customer_id: newCustomer ? null : form.customer_id,
        new_customer: newCustomer ? form.new_customer : null,
      });
      toast.success("Vente enregistrée — bénéfice calculé automatiquement");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submitPay = async () => {
    try {
      await api.post(`/sales/${payFor.id}/payments`, { amount: Number(payForm.amount), account_id: payForm.account_id || null, date: payForm.date });
      toast.success("Paiement enregistré");
      setPayFor(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="sales-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Ventes</h1>
          <p className="mt-1 text-sm text-slate-500">Ventes comptant, à crédit et paiements partiels — factures et reçus PDF automatiques.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-sale-button"><Plus className="mr-1 h-4 w-4" /> Nouvelle vente</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Enregistrer une vente</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex items-center justify-between">
                <Label>Client *</Label>
                <button type="button" className="text-xs font-medium text-blue-600 hover:underline" onClick={() => setNewCustomer(!newCustomer)} data-testid="toggle-new-customer">
                  {newCustomer ? "Client existant" : "+ Nouveau client"}
                </button>
              </div>
              {newCustomer ? (
                <>
                  <div><Label>Nom complet</Label><Input value={form.new_customer.name} onChange={(e) => set("new_customer", { ...form.new_customer, name: e.target.value })} data-testid="sale-new-customer-name" /></div>
                  <div><Label>Téléphone</Label><Input value={form.new_customer.phone} onChange={(e) => set("new_customer", { ...form.new_customer, phone: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Adresse</Label><Input value={form.new_customer.address} onChange={(e) => set("new_customer", { ...form.new_customer, address: e.target.value })} /></div>
                </>
              ) : (
                <div className="col-span-2">
                  <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                    <SelectTrigger data-testid="sale-customer-select"><SelectValue placeholder="Choisir un client…" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label>Véhicule *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => {
                  const veh = vehicles.find((x) => x.id === v);
                  setForm((f) => ({ ...f, vehicle_id: v, sale_price: veh?.target_sale_price ? String(veh.target_sale_price) : f.sale_price }));
                }}>
                  <SelectTrigger data-testid="sale-vehicle-select"><SelectValue placeholder="Choisir un véhicule…" /></SelectTrigger>
                  <SelectContent>{sellable.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model} · coût {fmtMoney(v.cost_basis)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Prix de vente (FCFA)</Label><Input type="number" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="sale-price-input" /></div>
              <div><Label>Remise (FCFA)</Label><Input type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} data-testid="sale-discount-input" /></div>
              <div><Label>Montant payé (FCFA)</Label><Input type="number" value={form.paid_amount} onChange={(e) => set("paid_amount", e.target.value)} data-testid="sale-paid-input" /></div>
              <div><Label>Mode de paiement</Label>
                <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Compte crédité</Label>
                <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="sale-account-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date de vente</Label><Input type="date" value={form.sale_date} onChange={(e) => set("sale_date", e.target.value)} /></div>
              <div><Label>Commercial</Label><Input value={form.salesperson} onChange={(e) => set("salesperson", e.target.value)} /></div>
              {remaining > 0 && (
                <>
                  <div><Label>Nombre d'échéances</Label><Input type="number" min="1" value={form.installments_count} onChange={(e) => set("installments_count", e.target.value)} data-testid="sale-installments-count" /></div>
                  <div><Label>Fréquence</Label>
                    <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                      <SelectTrigger data-testid="sale-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hebdomadaire">Hebdomadaire</SelectItem>
                        <SelectItem value="bimensuelle">Toutes les 2 semaines</SelectItem>
                        <SelectItem value="mensuelle">Mensuelle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Première échéance</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="sale-first-due" /></div>
                </>
              )}
              <div className="col-span-2 rounded-lg bg-slate-50 p-3 text-sm">
                Prix final : <span className="font-mono-num font-bold">{fmtMoney(finalPrice)}</span>
                {remaining > 0 && <> · Reste à crédit : <span className="font-mono-num font-bold text-red-600">{fmtMoney(remaining)}</span></>}
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="sale-submit-button">Enregistrer la vente</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="sales-table" rows={rows}
        searchKeys={["sale_number", "customer_name", "vehicle_label"]}
        columns={[
          { key: "sale_number", label: "N°", render: (r) => <span className="font-mono-num font-semibold">{r.sale_number}</span> },
          { key: "sale_date", label: "Date", render: (r) => fmtDate(r.sale_date) },
          { key: "customer_name", label: "Client" },
          { key: "vehicle_label", label: "Véhicule" },
          { key: "final_price", label: "Prix final", className: "text-right", render: (r) => <span className="font-mono-num font-semibold">{fmtMoney(r.final_price)}</span> },
          { key: "paid_amount", label: "Payé", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.paid_amount)}</span> },
          { key: "remaining", label: "Reste", className: "text-right", render: (r) => <span className={`font-mono-num font-semibold ${r.remaining > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(r.remaining)}</span> },
          { key: "status", label: "Statut", render: (r) => <StatusBadge status={r.status} /> },
          { key: "actions", label: "Documents", sortable: false, render: (r) => (
            <div className="flex gap-1">
              {r.remaining > 0 && <Button size="sm" variant="outline" data-testid={`add-payment-${r.sale_number}`} onClick={() => { setPayFor(r); setPayForm({ amount: String(r.remaining), account_id: "", date: todayISO() }); }}>Paiement</Button>}
              <Button size="sm" variant="ghost" title="Facture PDF" onClick={() => openPdf(`/sales/${r.id}/invoice.pdf`)} data-testid={`invoice-pdf-${r.sale_number}`}><FileText className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" title="Reçu PDF" onClick={() => openPdf(`/sales/${r.id}/receipt.pdf`)} data-testid={`receipt-pdf-${r.sale_number}`}><Receipt className="h-4 w-4" /></Button>
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" title="Supprimer la vente" onClick={() => delSale(r)} data-testid={`delete-sale-${r.sale_number}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!payFor} onOpenChange={() => setPayFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Paiement — {payFor?.sale_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Reste à payer : <span className="font-mono-num font-bold text-red-600">{fmtMoney(payFor?.remaining)}</span></p>
            <div><Label>Montant (FCFA)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="sale-payment-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
            <div><Label>Compte crédité</Label>
              <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="sale-payment-account"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submitPay} data-testid="sale-payment-submit">Valider</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
